import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {shallowEqual, useSelector} from 'react-redux';
import {Swiper, SwiperSlide} from 'swiper/react';
// @ts-ignore
import {NavLink} from '@nl/core';
// @ts-ignore
import {Nav} from '@nl/component';
// @ts-ignore
import {useI18n} from '@nl/i18n';
import MVPDBrand from '../components/myaccount/MVPDBrand';
import getRouterConfig from '../utils/getRouterConfig';
import {event, EVENT_TYPES} from '../utils/event';
import {getURLParamByName} from '../utils/utils';

import 'swiper/swiper.scss';
import '../styles/header/header.scss';

const NO_HEADER_PAGE_PATHS = ['/streaming-subscriptions', '/packages'];

const getMenuItems = (pathname: string, isAccountPage: boolean, locServer: string,
											 watchSitePrefix: string): IMenuItems => {
	const isExternalPage: boolean = getRouterConfig(pathname, 'externalPage');
	const isNBAAccountPage: boolean = pathname === '/account/nbaprofile' || pathname === '/account/nbafavorites';

	const homeMenuConfig: IMenuItems = [
		{
			path: `${isExternalPage ? watchSitePrefix : '/'}featured`,
			pathMatch: ['/featured'],
		},
		{
			path: `${isExternalPage ? watchSitePrefix : '/'}nba-tv`,
			pathMatch: ['/nba-tv', '/channel/nbatvlive', '/nba-tv/series', '/page/show'],
		},
		{
			path: `${isExternalPage ? watchSitePrefix : '/'}league-pass`,
			pathMatch: ['/league-pass', '/event/'],
		},
	];

	const accountMenuConfig: IMenuItems = [
		{
			path: `${locServer}account/profile`,
			pathMatch: ['/account/profile'],
		},
		{
			path: `${locServer}account/favorites#team`,
			pathMatch: ['#team'],
			className: 'fav-team',
		},
		{
			path: `${locServer}account/favorites#player`,
			pathMatch: ['#player'],
			className: 'fav-player',
		},
		{
			path: `${locServer}account/history`,
			pathMatch: ['/account/history'],
		},
	];

	const nbaAccountMenuConfig: IMenuItems = [
		{
			path: `${locServer}account/nbaprofile`,
			pathMatch: ['/account/nbaprofile'],
		},
		{
			path: `${locServer}account/nbafavorites#team`,
			pathMatch: ['#team'],
			className: 'fav-team',
		},
		{
			path: `${locServer}account/nbafavorites#player`,
			pathMatch: ['#player'],
			className: 'fav-player',
		},
	];

	if (isNBAAccountPage) {
		return nbaAccountMenuConfig;
	}
	return isAccountPage ? accountMenuConfig : homeMenuConfig;
};

interface IResults {
	categoryPrimary?: {
		bannerImage?: string,
		isHub?: boolean,
		menu?: {
			name: string,
			links: Array<{
				text: string,
				href: string,
			}>,
		},
	},
}

interface IHubInfo {
	name?: string,
	isHub?: boolean,
	bannerImage?: string,
	menuLabels?: IMenuLabels,
	menuItems?: IMenuItems,
}

type IMenuLabels = Array<string>;

type IMenuItems = Array<IMenuItem>;

interface IMenuItem {
	path: string,
	pathMatch?: Array<string>,
	className?: string,
}

const getHubInfo = (results: IResults): IHubInfo => {
	const hubInfo: IHubInfo = {};
	if (results?.categoryPrimary) {
		const {bannerImage, isHub, menu} = results.categoryPrimary;
		if (isHub) {
			hubInfo.isHub = isHub;
			const hubMenuLabels: IMenuLabels = [];
			const hubMenuItems: IMenuItems = [];
			if (bannerImage) {
				hubInfo.bannerImage = bannerImage;
			}
			if (menu) {
				const {name, links} = menu;
				hubInfo.name = name;
				if (links && links.length > 0) {
					for (let i = 0; i < links.length; i += 1) {
						hubMenuLabels.push(links[i].text);
						hubMenuItems.push({path: links[i].href});
					}
				}
			}
			if (hubMenuLabels.length > 0) {
				hubInfo.menuLabels = hubMenuLabels;
			}
			if (hubMenuItems.length > 0) {
				hubInfo.menuItems = hubMenuItems;
			}
		}
	}
	return hubInfo;
};

function Header() {
	const {pathname, hash, search} = useLocation();
	const {
		headerSwiperConfig,
		LOC_SERVER,
		WATCH_SITE_PREFIX,
	} = useSelector((state: any) => ({
		headerSwiperConfig: state.config.data.OWL_OPTIONS.headerOwl,
		LOC_SERVER: state.config.data.LOC_SERVER,
		WATCH_SITE_PREFIX: state.config.data.WATCH_SITE_PREFIX,
	}), shallowEqual);

	const isAccountPage = pathname === '/account/profile' || pathname === '/account/favorites'
			|| pathname === '/account/history' || pathname === '/account/nbaprofile'
			|| pathname === '/account/nbafavorites';

	const defaultMenuTitleKey = isAccountPage ? 'header_menu_account_my_account' : 'header_menu_watch';
	const [defaultMenuTitle] = useI18n(defaultMenuTitleKey);
	const [menuTitle, setMenuTitle] = useState<string>(defaultMenuTitle);

	const menuItemsKeyArr = isAccountPage
			? ['account_manage_account', 'account_favorite_teams', 'account_favorite_players', 'account_viewing_history']
			: ['featured', 'nba_tv', 'league_pass'];
	const defaultMenuItemsArr = useI18n(menuItemsKeyArr, {keyPrefix: 'header_menu_'});
	const [menuItemsArr, setMenuItemsArr] = useState<IMenuLabels>([]);

	const defaultMenuItems = useMemo((): IMenuItems => getMenuItems(
			pathname,
			isAccountPage,
			LOC_SERVER,
			WATCH_SITE_PREFIX,
	), [pathname, isAccountPage, LOC_SERVER, WATCH_SITE_PREFIX]);
	const [menuItems, setMenuItems] = useState<IMenuItems>(defaultMenuItems);
	const [selectedMenuIndex, setSelectedMenuIndex] = useState<number>(-1);

	const [bannerImage, setBannerImage] = useState<string>('');
	const styleComponentBanner = {
		backgroundImage: `url(${bannerImage})`,
	};
	const [hubInfo, setHubInfo] = useState<IHubInfo | null>(null);
	const [hideNav, setHideNav] = useState(false);

	const menuItemsList = useMemo(() => (menuItems.map((item: IMenuItem, index) => (
			<SwiperSlide>
				<Nav.Item key={`${item.path}`} className={`${item.className || ''}`}>
					<NavLink key={`${item.path}`}
									 className={index === selectedMenuIndex ? 'is-selected' : ''}
									 to={item.path}>
						{menuItemsArr[index]}
					</NavLink>
				</Nav.Item>
			</SwiperSlide>
	))), [menuItems, menuItemsArr, selectedMenuIndex]);

	const getActiveMenuIndex = useCallback((menuItemsDataArr: IMenuItems, path): number => {
		if (path !== '/') {
			for (let i = 0; i < menuItemsDataArr.length; i += 1) {
				const menuItem = menuItemsDataArr[i];
				if (menuItem.pathMatch) {
					for (const match of menuItem.pathMatch) {
						if (hash && hash === match) {
							return i;
						}
						if (path.indexOf(match) === 0) {
							return i;
						}
					}
				}
			}
			return -1;
		}
		return 0;
	}, [hash]);

	useEffect(() => {
		setMenuItems(hubInfo?.menuItems || defaultMenuItems);
	}, [hubInfo, defaultMenuItems]);

	useEffect(() => {
		setMenuTitle(hubInfo?.name || defaultMenuTitle);
	}, [hubInfo, defaultMenuTitle]);

	useEffect(() => {
		setMenuItemsArr(hubInfo?.menuLabels || defaultMenuItemsArr);
	}, [hubInfo, defaultMenuItemsArr]);

	useEffect(() => {
		setBannerImage(hubInfo?.bannerImage || '');
	}, [hubInfo]);

	useEffect(() => {
		const dataCallback = (results: IResults) => {
			const info = getHubInfo(results);
			setHubInfo(info);
		};

		event.on(EVENT_TYPES.VIDEO_DETAIL_NBA_FEED_READY, dataCallback);

		return () => {
			event.off(EVENT_TYPES.VIDEO_DETAIL_NBA_FEED_READY, dataCallback);
		};
	}, []);

	useEffect(() => {
		const activeMenuIndex = getActiveMenuIndex(menuItems, pathname);
		setSelectedMenuIndex(activeMenuIndex);
		// eslint-disable-next-line
	}, [menuItems, pathname, hash]);

	useEffect(() => {
		setHideNav(getURLParamByName('hidenav', search) === 'true');
	}, [search]);

	if ((NO_HEADER_PAGE_PATHS.indexOf(pathname) !== -1) || (!hubInfo && hideNav)) {
		return <></>;
	}
	return (
			<>
				<div className="header">
					<div className="nav-bar">
						<div className="nav-bar__text">{menuTitle}</div>
						<div className="nav-bar__line" />
						<Nav fill id="subMenus">
							<Swiper {...headerSwiperConfig}>
								{menuItemsList}
							</Swiper>
						</Nav>
						<MVPDBrand />
					</div>
				</div>
				<div className="nav-header-placeholder" />
				{bannerImage && <div className="banner-image" style={styleComponentBanner} />}
			</>
	);
}

export default Header;
